import { forwardRef, useEffect, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { BookOpen, IndianRupee, Layers, School } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { instituteToday } from '@/features/teacher/utils';
import { formatCurrency } from '@/lib/format';
import type { FeeHeadLedger, FeeReceipt, StudentFeeAccount } from '@/types/fees';
import { feesApi } from '../api/fees.api';
import { FEE_METHOD_OPTIONS, FEE_METHOD_REFERENCE_HINT } from '../constants';
import {
  paymentFormResolver,
  paymentSchema,
  type PaymentFormValues,
} from '../schemas/fee.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/** The heads in the order a lump sum pays them off — tuition first, as schools collect it. */
const HEAD_ORDER = ['tuition', 'books', 'other'] as const;
type HeadKey = (typeof HEAD_ORDER)[number];
const FIELD_BY_HEAD = {
  tuition: 'tuitionAmount',
  books: 'booksAmount',
  other: 'otherAmount',
} as const satisfies Record<HeadKey, keyof PaymentFormValues>;

/** What is left on a head, in whole rupees — the most one payment can take off it. */
function headBalance(account: StudentFeeAccount, head: HeadKey): number {
  return Math.max(0, Math.floor(Number(account[head].balance) || 0));
}

/**
 * Spreads one amount across the heads, filling each before the next.
 * Whatever is beyond the whole balance is left unassigned, and the caller
 * says so rather than inventing a head to put it on.
 */
function splitAcrossHeads(account: StudentFeeAccount, amount: number): Record<HeadKey, number> {
  let left = amount;
  const split = { tuition: 0, books: 0, other: 0 };
  for (const head of HEAD_ORDER) {
    split[head] = Math.min(left, headBalance(account, head));
    left -= split[head];
  }
  return split;
}

export interface CollectFeeModalProps {
  /** The account being paid into, or null while the dialog is closed. */
  account: StudentFeeAccount | null;
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  /** Gets the receipt and the account it left, so the page can confirm both. */
  onCollected: (receipt: FeeReceipt, account: StudentFeeAccount) => void;
}

/**
 * Taking money at the counter.
 *
 * One "Amount received" box for what the family is paying now — the whole
 * balance by default, or any part of it for an instalment. Typing it spreads
 * the money over the heads, tuition first, into the per-head boxes below, which
 * the office can still adjust for a family paying a particular fee. The account
 * is kept per head, so every receipt still says which head it cleared.
 *
 * The receipt number, the amounts and whether they fit inside the balance are
 * the server's, recalculated before a receipt exists. Once it is issued the
 * account itself has moved, so the register, the student's record, the
 * dashboard and the parent panel all read the new balance — none keeps a copy.
 */
export function CollectFeeModal({
  account,
  isOpen,
  onClose,
  onBack,
  onCollected,
}: CollectFeeModalProps) {
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * The lump sum as typed. Held apart from the heads because the two are
   * edited from both ends: typing here re-splits the heads, and editing a head
   * sets this back to their sum.
   */
  const [received, setReceived] = useState('');

  const form = useForm<PaymentFormValues>({
    resolver: paymentFormResolver,
    defaultValues: {
      tuitionAmount: '0',
      booksAmount: '0',
      otherAmount: '0',
      method: 'CASH',
      reference: '',
      paidOn: instituteToday(),
      remarks: '',
    },
  });
  useLiveFormErrors(form);
  const {
    register,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const method = watch('method');
  const tuitionAmount = Number(watch('tuitionAmount') || 0);
  const booksAmount = Number(watch('booksAmount') || 0);
  const otherAmount = Number(watch('otherAmount') || 0);

  // Refilled whenever the dialog opens, so a half-typed receipt never appears
  // over the next family's account.
  useEffect(() => {
    if (!isOpen || !account) return;
    setFormError(null);
    // Opens on the whole balance: the ordinary payment clears the account, and
    // the office overtypes the amount for an instalment.
    const split = splitAcrossHeads(account, Number.MAX_SAFE_INTEGER);
    setReceived(String(split.tuition + split.books + split.other));
    form.reset({
      tuitionAmount: String(split.tuition),
      booksAmount: String(split.books),
      otherAmount: String(split.other),
      method: 'CASH',
      reference: '',
      paidOn: instituteToday(),
      remarks: '',
    });
  }, [isOpen, account, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!account) return;
    setFormError(null);

    try {
      const payload = paymentSchema.parse(values);
      const { receipt, account: updated } = await feesApi.recordPayment(
        account.student.uuid,
        payload,
      );
      // The page raises the confirmation once this dialog has closed — a toast
      // under an open `<dialog>` sits behind it, in the browser's top layer.
      onCollected(receipt, updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        // The server names the reason — more than is outstanding, or a fee
        // record that was never opened — so it is shown as it came.
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  const total = tuitionAmount + booksAmount + otherAmount;
  const outstanding = account
    ? HEAD_ORDER.reduce((sum, head) => sum + headBalance(account, head), 0)
    : 0;
  const receivedValue = Number(received || 0);
  const receivedError = !/^\d*$/.test(received)
    ? 'Whole rupees only'
    : receivedValue > outstanding
      ? `More than the ${formatCurrency(outstanding)} outstanding`
      : undefined;

  function changeReceived(next: string) {
    setReceived(next);
    if (!account || !/^\d*$/.test(next)) return;
    const split = splitAcrossHeads(account, Number(next || 0));
    for (const head of HEAD_ORDER) {
      form.setValue(FIELD_BY_HEAD[head], String(split[head]), { shouldValidate: true });
    }
  }

  /** A head edited by hand: the lump sum follows it. */
  const headField = (field: (typeof FIELD_BY_HEAD)[HeadKey]) =>
    register(field, {
      onChange: () => {
        const values = form.getValues();
        setReceived(
          String(
            HEAD_ORDER.reduce((sum, head) => sum + (Number(values[FIELD_BY_HEAD[head]]) || 0), 0),
          ),
        );
      },
    });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onBack={onBack}
      size="lg"
      title={account ? `Record Fee Payment - ${account.student.fullName}` : 'Record Fee Payment'}
      description={
        account
          ? `Outstanding: ${formatCurrency(account.balance)} · Academic Year ${account.academicYear.name}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting} className="rounded-xl font-bold text-sm">
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            isLoading={isSubmitting}
            disabled={Boolean(receivedError)}
            className="rounded-xl px-5 font-bold text-sm bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs transition-colors"
          >
            {isSubmitting ? 'Recording payment…' : `Record Payment of ${formatCurrency(total)}`}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not record this payment">
            {formError}
          </Alert>
        )}

        <Input
          label="Payment Amount"
          labelClassName="text-sm font-bold text-slate-700 dark:text-slate-200"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={received}
          onChange={(event) => changeReceived(event.target.value.trim())}
          leftIcon={<IndianRupee className="h-4 w-4 text-slate-400" />}
          hint="Enter the amount being paid now. Any remaining balance will stay outstanding."
          error={receivedError}
          className="font-mono !rounded-xl !h-11 !text-base !font-bold !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
        />

        <div className="flex flex-col gap-2">
          <div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
              Fee Breakdown
            </p>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">
              The amount is allocated to each fee category below. Adjust the amounts if needed.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <HeadAmountField
              title="Tuition Fee"
              icon={<School className="h-3.5 w-3.5" aria-hidden />}
              head={account?.tuition}
              error={errors.tuitionAmount?.message}
              {...headField('tuitionAmount')}
            />
            <HeadAmountField
              title="Books & Study Materials"
              icon={<BookOpen className="h-3.5 w-3.5" aria-hidden />}
              head={account?.books}
              error={errors.booksAmount?.message}
              {...headField('booksAmount')}
            />
            <HeadAmountField
              title="Other Fees"
              icon={<Layers className="h-3.5 w-3.5" aria-hidden />}
              head={account?.other}
              error={errors.otherAmount?.message}
              {...headField('otherAmount')}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Payment Method"
            labelClassName="text-sm font-bold text-slate-700 dark:text-slate-200"
            className="!rounded-xl !h-10 !text-sm !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={FEE_METHOD_OPTIONS}
            error={errors.method?.message}
            {...register('method')}
          />
          <DatePicker
            label="Payment Date"
            labelClassName="text-sm font-bold text-slate-700 dark:text-slate-200"
            className="!rounded-xl !h-10 !text-sm !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            max={instituteToday()}
            hint="Select the date the payment was received."
            error={errors.paidOn?.message}
            {...register('paidOn')}
          />
        </div>

        <Input
          label={method === 'CASH' ? 'Payment Reference (Optional)' : 'Transaction or cheque number'}
          labelClassName="text-sm font-bold text-slate-700 dark:text-slate-200"
          placeholder={method === 'CHEQUE' ? '004512 — State Bank of India' : 'Enter receipt or transaction reference'}
          autoComplete="off"
          hint={FEE_METHOD_REFERENCE_HINT[method]}
          error={errors.reference?.message}
          className="!rounded-xl !h-10 !text-sm !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
          {...register('reference')}
        />

        <Textarea
          label="Note (optional)"
          labelClassName="text-sm font-bold text-slate-700 dark:text-slate-200"
          rows={2}
          placeholder="Second instalment, collected at the counter"
          hint="Printed on the receipt and kept with the account."
          error={errors.remarks?.message}
          className="!rounded-xl !text-sm !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
          {...register('remarks')}
        />
      </form>
    </Modal>
  );
}

interface HeadAmountFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  title: string;
  icon: ReactNode;
  /** What this head stands at, so the box can say what is left on it. */
  head: FeeHeadLedger | undefined;
  error?: string;
}

const HeadAmountField = forwardRef<HTMLInputElement, HeadAmountFieldProps>(
  function HeadAmountField({ title, icon, head, error, ...field }, ref) {
    const outstanding = Number(head?.balance ?? 0);

    return (
      <div className="rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/70 dark:bg-[#0e1726] p-4">
        <h3 className="font-bold text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 uppercase tracking-wide">
          {icon}
          {title}
        </h3>

        <div className="mt-3">
          <Input
            ref={ref}
            label={`${title} paid now`}
            hideLabel
            type="number"
            inputMode="numeric"
            min={0}
            className="font-mono !rounded-xl !h-10 !text-sm !font-bold !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            leftIcon={<IndianRupee className="h-4 w-4 text-slate-400" />}
            error={error}
            {...field}
          />
        </div>

        <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-400">
          {outstanding > 0
            ? `${formatCurrency(head?.balance)} remaining out of ${formatCurrency(head?.payable)}`
            : 'Nothing outstanding on this head'}
        </p>
      </div>
    );
  },
);
